package com.milta.feature.route.presentation

import com.milta.core.appconfig.AppConfigRepository
import com.milta.core.mvi.MviViewModel
import com.milta.core.network.ApiException
import com.milta.feature.imports.domain.entity.ImportPdfResult
import com.milta.feature.imports.domain.usecase.ExtractDeliveriesFromPdfUseCase
import com.milta.feature.route.domain.entity.Delivery
import com.milta.feature.route.domain.entity.RouteBatchResult
import com.milta.feature.route.domain.usecase.CreateRouteBatchUseCase
import com.milta.feature.route.domain.usecase.DispatchRouteBatchUseCase
import com.milta.feature.route.domain.usecase.GetRouteDetailUseCase
import com.milta.feature.spreadsheetimport.domain.entity.SpreadsheetImportResult
import com.milta.feature.spreadsheetimport.domain.usecase.ImportSpreadsheetUseCase
import com.milta.feature.team.domain.usecase.GetEnterpriseDriversUseCase
import com.milta.feature.team.domain.usecase.GetMyTeamUseCase
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch

private const val HTTP_BAD_REQUEST = 400
private const val HTTP_UNPROCESSABLE_ENTITY = 422
private const val HTTP_NOT_FOUND = 404
private const val HTTP_CONFLICT = 409
private const val HTTP_SERVICE_UNAVAILABLE = 503

/**
 * ViewModel for the web dashboard's "Ajouter une tournée" flow (MIL-50/MIL-51, mockup
 * `docs/mockups/MIL-50.html`) — a three-step wizard (Écran 1 "Importer" / Écran 2 "Vérifier &
 * assigner" / Écran 3 "Valider et envoyer les tournées") driving all three screens off a single
 * [WebImportRouteState.phase]. Reuses the same use cases already wired for the mobile Gestionnaire
 * flow ([CreateRouteBatchUseCase]/[DispatchRouteBatchUseCase]/[GetRouteDetailUseCase]/
 * [GetMyTeamUseCase]/[GetEnterpriseDriversUseCase], see `CreateRouteScreen` (multi-driver selection
 * merged into it)/`RouteBatchPreviewScreen`) rather than duplicating them — only [ImportSpreadsheetUseCase] and the
 * wizard state itself are new.
 *
 * Note (MIL-50 scope update, 2026-08-20): PDF is now accepted too, routed to the same
 * [ExtractDeliveriesFromPdfUseCase]/`POST /imports/pdf` already wired for mobile (see
 * `ImportPdfValidationViewModel`) rather than a new extraction service — `.pdf` files branch off
 * [importFile] before reaching [importSpreadsheet], everything downstream (rows, Écran 2) is
 * unchanged since both paths map to the same [WebImportRouteRow].
 *
 * Note (MIL-51, multi-driver): Écran 2's submit now always goes through `POST /route-batches`
 * (MIL-39 contract) — even for a single selected driver, no N=1 special case — and no longer
 * dispatches immediately: a successful create moves to Écran 3 ([WebImportRoutePhase.BatchResult])
 * so the manager reviews the split before "Valider et envoyer les tournées" fires the actual
 * `POST /route-batches/{id}/dispatch`. The single-route `CreateRouteUseCase`/`DispatchRouteUseCase`
 * pair used by this ViewModel before MIL-51 is gone — see `CreateRouteViewModel`/`DispatchViewModel`
 * for where that pair still lives, unrelated to this web wizard.
 *
 * Note (RoutaR-unavailable fallback): when [submit] fails with `optimization_unavailable`, Écran 2d
 * offers a second action ([WebImportRouteIntent.SubmitWithoutOptimization]) that retries the same
 * `POST /route-batches` with `optimize = false` — the round-robin split already implemented for
 * MIL-39, just never wired to the web wizard before. Not an automatic fallback: the manager decides.
 */
class WebImportRouteViewModel(
    private val importSpreadsheet: ImportSpreadsheetUseCase,
    private val extractDeliveriesFromPdf: ExtractDeliveriesFromPdfUseCase,
    private val getMyTeam: GetMyTeamUseCase,
    private val getEnterpriseDrivers: GetEnterpriseDriversUseCase,
    private val createRouteBatch: CreateRouteBatchUseCase,
    private val dispatchRouteBatch: DispatchRouteBatchUseCase,
    private val getRouteDetail: GetRouteDetailUseCase,
    private val appConfigRepository: AppConfigRepository,
) : MviViewModel<WebImportRouteState, WebImportRouteIntent>(WebImportRouteState()) {

    private val _navEvent = Channel<WebImportRouteNavEvent>(Channel.BUFFERED)
    val navEvent = _navEvent.receiveAsFlow()

    private var lastFileBytes: ByteArray = ByteArray(0)
    private var lastFileName: String = ""

    init {
        // Ad hoc feature (2026-08) — RoutaR kill switch: known ahead of time so Écran 2 never even
        // shows/attempts the "Optimiser l'itinéraire" CTA when it would fail every time.
        scope.launch {
            val routarEnabled = appConfigRepository.isRoutarEnabled()
            setState { copy(routarEnabled = routarEnabled) }
        }
    }

    override fun onIntent(intent: WebImportRouteIntent) {
        when (intent) {
            is WebImportRouteIntent.PickFile -> importFile(intent.fileBytes, intent.fileName)
            WebImportRouteIntent.RetryServiceCall -> importFile(lastFileBytes, lastFileName)
            is WebImportRouteIntent.ToggleDriver -> toggleDriver(intent.driverId)
            is WebImportRouteIntent.UpdateRow -> setState {
                copy(rows = rows.map { if (it.id == intent.rowId) intent.row else it })
            }
            WebImportRouteIntent.Submit -> submit(optimize = true)
            WebImportRouteIntent.SubmitWithoutOptimization -> submit(optimize = false)
            WebImportRouteIntent.ValidateAndSend -> validateAndSend()
            WebImportRouteIntent.ClearSubmitError -> setState { copy(submitError = null) }
        }
    }

    private fun toggleDriver(driverId: String) {
        setState {
            val updated = if (driverId in selectedDriverIds) selectedDriverIds - driverId else selectedDriverIds + driverId
            copy(selectedDriverIds = updated, submitError = null)
        }
    }

    /** `.pdf` routes to [extractDeliveriesFromPdf] (`POST /imports/pdf`, same endpoint already
     * wired for mobile) — every other extension keeps going through [importSpreadsheet]
     * (`POST /spreadsheet-imports`, .xlsx/.xls/.csv), unchanged. */
    private fun importFile(fileBytes: ByteArray, fileName: String) {
        lastFileBytes = fileBytes
        lastFileName = fileName
        // routarEnabled is app-level config, not tied to this import session — preserved across
        // the otherwise full state reset below (re-import/retry).
        setState { WebImportRouteState(phase = WebImportRoutePhase.Analysing, fileName = fileName, routarEnabled = routarEnabled) }
        scope.launch {
            if (fileName.endsWith(".pdf", ignoreCase = true)) {
                runCatching { extractDeliveriesFromPdf(fileBytes, fileName) }.fold(
                    onSuccess = { result -> onPdfImportSucceeded(result) },
                    onFailure = { error -> handleImportFailure(error) },
                )
            } else {
                runCatching { importSpreadsheet(fileBytes, fileName) }.fold(
                    onSuccess = { result -> onImportSucceeded(result) },
                    onFailure = { error -> handleImportFailure(error) },
                )
            }
        }
    }

    /** [MAX_STOPS] is checked here — right after import — rather than only at [submit] time, so an
     * oversized file is rejected before the manager spends time reviewing/correcting rows. */
    private fun onImportSucceeded(result: SpreadsheetImportResult) {
        val rows = result.deliveries.mapIndexed { index, row -> row.toRouteRow(index) }
        if (rows.size > MAX_STOPS) {
            setState { copy(phase = WebImportRoutePhase.TooManyStops(rows.size)) }
            return
        }
        setState {
            copy(
                phase = WebImportRoutePhase.VerifyAssign,
                totalCount = result.totalCount,
                isStructuredFormat = result.isStructuredFormat,
                rows = rows,
            )
        }
        loadDrivers()
    }

    /** Mirrors [onImportSucceeded] for the PDF path — [ImportPdfResult] has no `incompleteCount`/
     * `isStructuredFormat` of its own (unlike [SpreadsheetImportResult]): incompleteness is
     * recomputed off [WebImportRouteRow.isIncomplete] (same rule already used by the mobile PDF
     * validation screen, `ExtractedDeliveryRowUi.isComplete`), and PDF rows never trigger the
     * "free format" banner — that's a spreadsheet-only ambiguous-column concept. */
    private fun onPdfImportSucceeded(result: ImportPdfResult) {
        val rows = result.deliveries.mapIndexed { index, delivery -> delivery.toRouteRow(index) }
        if (rows.size > MAX_STOPS) {
            setState { copy(phase = WebImportRoutePhase.TooManyStops(rows.size)) }
            return
        }
        setState {
            copy(
                phase = WebImportRoutePhase.VerifyAssign,
                totalCount = result.totalExtracted,
                isStructuredFormat = true,
                rows = rows,
            )
        }
        loadDrivers()
    }

    private fun handleImportFailure(error: Throwable) {
        setState {
            copy(
                phase = when {
                    error is ApiException && error.statusCode == HTTP_BAD_REQUEST -> WebImportRoutePhase.InvalidFile
                    error is ApiException && error.statusCode == HTTP_UNPROCESSABLE_ENTITY -> WebImportRoutePhase.ExtractionFailed
                    error is ApiException -> WebImportRoutePhase.ServiceUnavailable
                    else -> WebImportRoutePhase.NetworkUnavailable
                },
            )
        }
    }

    /** Mirrors `CreateRouteViewModel.load()` — the enterprise dashboard has exactly one
     * enterprise membership for the connected account, so the first one is used. A failure here is
     * silent (empty driver list, no dedicated error banner in the mockup) rather than blocking the
     * whole Écran 2 — the manager can still review/correct rows while retrying isn't offered. */
    private fun loadDrivers() {
        scope.launch {
            setState { copy(isLoadingDrivers = true) }
            val enterpriseId = getMyTeam().getOrNull()?.firstOrNull()?.enterpriseId
            if (enterpriseId == null) {
                setState { copy(isLoadingDrivers = false) }
                return@launch
            }
            getEnterpriseDrivers(enterpriseId).fold(
                onSuccess = { drivers -> setState { copy(isLoadingDrivers = false, drivers = drivers) } },
                onFailure = { setState { copy(isLoadingDrivers = false) } },
            )
        }
    }

    /** Écran 2 → Écran 3 — always `POST /route-batches` (MIL-39), never the single-route endpoint,
     * regardless of [WebImportRouteState.selectedDriverIds]' size. A 503 with [optimize] `true` means
     * RoutaR itself is down: the manager stays on Écran 2, sees `optimization_unavailable`, and can
     * either retry the same optimized call once RoutaR is back, or fire
     * [WebImportRouteIntent.SubmitWithoutOptimization] ([optimize] `false`) to send the round-robin
     * split immediately instead of waiting. */
    private fun submit(optimize: Boolean) {
        if (currentState.isSubmitting) return
        if (currentState.selectedDriverIds.isEmpty()) {
            setState { copy(submitError = "driver_required") }
            return
        }
        // Guard set synchronously, before scope.launch (Dispatchers.Default dispatches
        // asynchronously) — closes the double-tap window that let two batch-create calls fire.
        setState { copy(isSubmitting = true, submitError = null) }
        scope.launch {
            // Ad hoc feature (2026-08) — RoutaR kill switch: when known off ahead of time, force the
            // round-robin split outright rather than attempting a call that would fail every time —
            // equivalent to the manager firing SubmitWithoutOptimization themselves.
            val effectiveOptimize = optimize && currentState.routarEnabled
            runCatching {
                val drafts = currentState.rows.map { it.toDraft() }
                createRouteBatch(currentState.selectedDriverIds.toList(), drafts, optimize = effectiveOptimize)
            }.fold(
                onSuccess = { result ->
                    setState { copy(isSubmitting = false, phase = WebImportRoutePhase.BatchResult, batchResult = result) }
                    loadBatchStops(result)
                },
                onFailure = { e -> setState { copy(isSubmitting = false, submitError = mapBatchError(e)) } },
            )
        }
    }

    /** `RouteBatchResultDto` carries no per-stop detail (name/address), only counts (MIL-39) — one
     * `GET /routes/{id}` (MIL-38, [getRouteDetail]) per driver's freshly created route fills in the
     * "first 4 stops" preview on Écran 3. A per-route failure here yields an empty stop list for
     * that one driver rather than failing the whole screen — the counts/distance/duration on its
     * card (from [WebImportRouteState.batchResult] itself) stay correct either way — but the failed
     * route id is tracked in [WebImportRouteState.batchStopsFailedRouteIds] so the card can show a
     * "couldn't load" message instead of an indistinguishable empty preview. */
    private fun loadBatchStops(result: RouteBatchResult) {
        scope.launch {
            setState { copy(isLoadingBatchStops = true) }
            val stopsByRouteId = mutableMapOf<String, List<Delivery>>()
            val failedRouteIds = mutableSetOf<String>()
            result.drivers.forEach { proposal ->
                val routeDetail = runCatching { getRouteDetail(proposal.routeId) }.getOrNull()
                if (routeDetail == null) failedRouteIds += proposal.routeId
                stopsByRouteId[proposal.routeId] = routeDetail?.deliveries?.sortedBy { it.sortOrder }.orEmpty()
            }
            setState {
                copy(
                    isLoadingBatchStops = false,
                    batchStopsByRouteId = stopsByRouteId,
                    batchStopsFailedRouteIds = failedRouteIds,
                )
            }
        }
    }

    /** Écran 3 — "Valider et envoyer les tournées": `POST /route-batches/{id}/dispatch` (MIL-39),
     * scheduling every route of the batch in one action. No re-optimization intent from this step
     * (out of MIL-51's scope) — a failure here leaves [WebImportRouteState.batchResult] untouched so
     * the manager can simply retry. */
    private fun validateAndSend() {
        if (currentState.isDispatchingBatch) return
        val batchId = currentState.batchResult?.batchId ?: return
        // Guard set synchronously, before scope.launch (Dispatchers.Default dispatches
        // asynchronously) — closes the double-tap window that let two dispatch calls fire.
        setState { copy(isDispatchingBatch = true, submitError = null) }
        scope.launch {
            runCatching { dispatchRouteBatch(batchId) }.fold(
                onSuccess = {
                    setState { copy(isDispatchingBatch = false) }
                    _navEvent.send(WebImportRouteNavEvent.Dispatched)
                },
                onFailure = { e -> setState { copy(isDispatchingBatch = false, submitError = mapBatchError(e)) } },
            )
        }
    }

    private fun mapBatchError(e: Throwable): String = when {
        e is ApiException && e.statusCode == HTTP_CONFLICT -> "already_dispatched"
        e is ApiException && e.statusCode == HTTP_NOT_FOUND -> "route_not_found"
        e is ApiException && e.statusCode == HTTP_BAD_REQUEST -> "validation_error"
        e is ApiException && e.statusCode == HTTP_SERVICE_UNAVAILABLE -> "optimization_unavailable"
        e is ApiException -> "server_error"
        else -> "offline"
    }
}
